import * as React from 'react';
import { FileUpload } from '@components/base/inputs';
import { VStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// Map accept prop values to actual accept strings
const acceptMap: Record<string, string | undefined> = {
  any: undefined,
  images: 'image/*',
  documents: '.pdf,.doc,.docx,.txt',
  pdf: '.pdf',
};

function FileUploadShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [singleFile, setSingleFile] = React.useState<File[]>([]);
  const [multipleFiles, setMultipleFiles] = React.useState<File[]>([]);
  const [imageFiles, setImageFiles] = React.useState<File[]>([]);

  const acceptValue = acceptMap[playgroundProps.accept as string] || undefined;

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="file upload">
          <div className="max-w-lg">
            <FileUpload
              label="Upload Documents"
              multiple={playgroundProps.multiple as boolean}
              disabled={playgroundProps.disabled as boolean}
              accept={acceptValue}
              value={singleFile}
              onChange={setSingleFile}
              helperText="Drag and drop files or click to browse"
            />
          </div>
      </ShowcasePreviewSection>

      {/* Single File Upload */}
      <ShowcaseDemoSection
        title="Single File Upload"
        description="Upload a single file at a time"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Policy Document"
              helperText="Upload your signed policy document"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Multiple Files */}
      <ShowcaseDemoSection
        title="Multiple File Upload"
        description="Upload multiple files at once"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Supporting Documents"
              multiple
              maxFiles={5}
              value={multipleFiles}
              onChange={setMultipleFiles}
              helperText="Upload up to 5 supporting documents"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Image Upload with Preview */}
      <ShowcaseDemoSection
        title="Image Upload with Preview"
        description="Upload images with thumbnail previews"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Claim Photos"
              accept="image/*"
              multiple
              maxFiles={10}
              showPreview
              value={imageFiles}
              onChange={setImageFiles}
              helperText="Upload photos of the damage (max 10)"
            />
          </div>
      </ShowcaseDemoSection>

      {/* PDF Only */}
      <ShowcaseDemoSection
        title="PDF Documents Only"
        description="Restrict uploads to PDF files"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Insurance Certificate"
              accept=".pdf"
              helperText="Only PDF files are accepted"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Document Types */}
      <ShowcaseDemoSection
        title="Document Types"
        description="Accept multiple document formats"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Legal Documents"
              accept=".pdf,.doc,.docx,.txt"
              multiple
              maxFiles={3}
              helperText="PDF, DOC, DOCX, or TXT files accepted"
            />
          </div>
      </ShowcaseDemoSection>

      {/* With Size Limit */}
      <ShowcaseDemoSection
        title="With File Size Limit"
        description="Restrict maximum file size"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Upload Photos"
              accept="image/*"
              maxSize={5 * 1024 * 1024}
              multiple
              maxFiles={5}
              helperText="Maximum 5MB per file"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Required */}
      <ShowcaseDemoSection
        title="Required Upload"
        description="Required file upload fields"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Proof of Insurance"
              required
              accept=".pdf,image/*"
              helperText="Please upload your current insurance card or certificate"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Error State */}
      <ShowcaseDemoSection
        title="Error State"
        description="File upload with validation error"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Claim Evidence"
              error
              errorMessage="At least one file is required to process your claim"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Disabled State */}
      <ShowcaseDemoSection
        title="Disabled State"
        description="Disabled file upload cannot be interacted with"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Locked Documents"
              disabled
              helperText="Document upload is not available at this time"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Insurance-specific Examples */}
      <ShowcaseDemoSection
        title="Vehicle Damage Photos"
        description="Upload photos for auto insurance claims"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Vehicle Damage Photos"
              accept="image/*"
              multiple
              maxFiles={8}
              maxSize={10 * 1024 * 1024}
              showPreview
              required
              helperText="Upload clear photos showing all damage to your vehicle. Include photos of the overall damage, close-ups, and your license plate."
            />
          </div>
      </ShowcaseDemoSection>

      {/* Medical Records */}
      <ShowcaseDemoSection
        title="Medical Records Upload"
        description="Upload medical documentation for health claims"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Medical Records"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              maxFiles={10}
              maxSize={15 * 1024 * 1024}
              helperText="Upload medical bills, receipts, and provider documentation. All files are encrypted and stored securely."
            />
          </div>
      </ShowcaseDemoSection>

      {/* Property Documentation */}
      <ShowcaseDemoSection
        title="Property Documentation"
        description="Upload property-related documents for home insurance"
       
      >
          <div className="max-w-lg">
            <FileUpload
              label="Property Documents"
              accept=".pdf,.doc,.docx"
              multiple
              maxFiles={5}
              helperText="Upload property deed, appraisal reports, or home inspection documents"
            />
          </div>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const FileUploadShowcase: ShowcaseDefinition = {
  id: 'file-upload',
  name: 'FileUpload',
  category: 'inputs',
  description: 'File selection and upload control',
  propsDefinition: [
    {
      name: 'multiple',
      type: 'boolean',
      defaultValue: false,
      description: 'Allow multiple file selection',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the file upload is disabled',
    },
    {
      name: 'accept',
      type: 'select',
      options: ['any', 'images', 'documents', 'pdf'],
      defaultValue: 'any',
      description: 'Accepted file types',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-label on dropzone',
      'aria-disabled (disabled state)',
      'aria-invalid (error state)',
      'aria-required (required state)',
      'aria-describedby (helper/error text)',
      'aria-label on remove buttons',
    ],
    keyboardNav: [
      'Tab: Move focus to dropzone',
      'Enter/Space: Open file dialog',
      'Tab to remove buttons for uploaded files',
    ],
    notes: [
      'Dropzone is keyboard accessible',
      'Drag and drop support with visual feedback',
      'File type and size validation',
      'Image preview support for visual confirmation',
      'Clear feedback for validation errors',
    ],
  },
  component: FileUploadShowcaseComponent,
};
